/**
 * projections
 *
 * Factory methods for constructing decorated d3 projections that are serializable and copyable. Also adds an
 * optimize() method which returns a faster implementation of the projection for temporary intensive use.
 */

import * as d3 from "../lib/d3.js";
import {orthographicProjection, orthographicArgsFromD3} from "./orthographic.js";
import {equirectangularProjection, equirectangularArgsFromD3} from "./equirectangular.js";


export const factories = Object.freeze({
    conicEquidistant: newConicEquidistant,
    equirectangular: newEquirectangular,
    mollweide: newMollweide,
    orthographic: newOrthographic,
    patterson: newPatterson,
    polyhedralWaterman: newPolyhedralWaterman,
    stereographic: newStereographic,
    winkel3: newWinkel3,
});

/**
 * @returns {{project: function, invert: function}} the default optimized interface for the projection.
 */
function defaultOptimize() {
    // The default optimization for a d3 projection that a) reuses the coords/point array to avoid creating garbage,
    // and b) guarantees a non-nullish result.
    const project_d3 = copy(this);
    const invert_d3 = project_d3.invert.bind(project_d3);
    const buffer = [NaN, NaN];
    return {
        project(lon, lat) {
            buffer[0] = lon;
            buffer[1] = lat;
            return project_d3(buffer) ?? [NaN, NaN];
        },
        invert(x, y) {
            buffer[0] = x;
            buffer[1] = y;
            return invert_d3(buffer) ?? [NaN, NaN];
        },
    };
}

function decorate(proj, key, optimize = defaultOptimize) {
    proj.key = key;
    proj.optimize = optimize;
    return proj;
}

export function newConicEquidistant() {
    return decorate(d3.geoConicEquidistant(), "conicEquidistant");
}

export function newEquirectangular() {
    return decorate(d3.geoEquirectangular(), "equirectangular", function optimize() {
        return equirectangularProjection(...equirectangularArgsFromD3(this));
    });
}

export function newMollweide() {
    return decorate(d3.geoMollweide(), "mollweide");
}

export function newOrthographic() {
    return decorate(d3.geoOrthographic(), "orthographic", function optimize() {
        return orthographicProjection(...orthographicArgsFromD3(this));
    });
}

export function newPatterson() {
    return decorate(d3.geoPatterson(), "patterson");
}

export function newPolyhedralWaterman() {
    return decorate(d3.geoPolyhedralWaterman(), "polyhedralWaterman");
}

export function newStereographic() {
    return decorate(d3.geoStereographic(), "stereographic");
}

export function newWinkel3() {
    return decorate(d3.geoWinkel3(), "winkel3");
}

/**
 * @param proj a decorated d3 projection.
 * @returns {object} the deconstructed form of the projection.
 */
export function deconstruct(proj) {
    if (factories[proj.key] === undefined) {
        throw new Error(`Unsupported projection: ${proj}`);
    }
    return {
        name:       proj.key,
        clipAngle:  proj.clipAngle(),
        clipExtent: proj.clipExtent(),
        scale:      proj.scale(),
        translate:  proj.translate(),
        center:     proj.center(),
        angle:      proj.angle(),
        reflectX:   proj.reflectX(),
        reflectY:   proj.reflectY(),
        rotate:     proj.rotate(),
        precision:  proj.precision(),
    };
}

/**
 * @param {object} payload a deconstructed d3 projection as generated by {@link deconstruct}.
 * @returns {Function} the reconstructed decorated d3 projection.
 */
export function reconstruct(payload) {
    const key = payload.name;
    const factory = factories[key];
    if (factory === undefined) {
        throw new Error(`Unknown projection: ${key}`);
    }
    const proj = factory();
    for (const [method, value] of Object.entries(payload)) {
        if (method === "name") {
            continue;
        }
        proj[method](value);
    }
    return proj;
}

/**
 * @param proj a decorated d3 projection.
 * @returns {Function} a copy of the projection.
 */
export function copy(proj) {
    return reconstruct(deconstruct(proj));
}
